import React, { useRef, useState, useEffect } from 'react';
import Transition from '@/utils/Transition';
import Close from '@/images/icons/Close';
import classNames from 'classnames';

function ModalAction({ id, children, modalOpen, size, setModalOpen, onModalClose = () => {}, hideClose = false }) {
  const modalContent = useRef(null);
  const dragging = useRef(false);
  const [positionY, setPositionY] = useState(0);
  const startYRef = useRef(0);
  const currentYRef = useRef(0);
  const animationFrameRef = useRef(null);

  const isMobile = window.innerWidth < 768;

  // Handle drag start
  const handleDragStart = (startPosition) => {
    if (!isMobile) return;

    dragging.current = true;
    startYRef.current = startPosition;
    currentYRef.current = positionY;
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleDragEnd);
    animationFrameRef.current = requestAnimationFrame(updatePosition);
  };

  // Handle drag end
  const handleDragEnd = () => {
    if (!isMobile || hideClose) return;

    dragging.current = false;
    cancelAnimationFrame(animationFrameRef.current);

    if (currentYRef.current > 100) {
      setModalOpen(false);
      onModalClose();
    }
    dragging.current = false;
    startYRef.current = 0;
    currentYRef.current = 0;

    setTimeout(() => {
      setPositionY(0);
    }, 400);

    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleDragEnd);
  };

  // Update position using requestAnimationFrame
  const updatePosition = () => {
    setPositionY(currentYRef.current);
    if (dragging.current) {
      animationFrameRef.current = requestAnimationFrame(updatePosition);
    }
  };

  // Handle touch move
  const handleTouchMove = (e) => {
    if (dragging.current) {
      const touch = e.touches[0];
      const deltaY = touch.clientY - startYRef.current;

      // Allow dragging up only if already dragged down but not above the initial position
      if ((deltaY > 0) || (deltaY < 0 && currentYRef.current > 0)) {
        startYRef.current = touch.clientY;
        currentYRef.current += deltaY;
      }
    }
  };

  // Start dragging on touch start
  const handleTouchStart = (e) => {
    handleDragStart(e.touches[0].clientY);
  };

  // Close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
	    if (!modalOpen || modalContent.current?.contains(target) || hideClose) return;
      if (target.closest('.rtite-root')) return;

      setModalOpen(false);
      onModalClose();
    };
    document.addEventListener('click', clickHandler, { capture: true });
    return () => document.removeEventListener('click', clickHandler, { capture: true });
  }, [modalOpen, setModalOpen]);

  // Close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!modalOpen || keyCode !== 27 || hideClose) return;
      setModalOpen(false);
      onModalClose();
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [modalOpen, setModalOpen]);

  return (
    <>
      {/* Modal backdrop */}
      <Transition
        className="fixed inset-0 bg-[#555555] bg-opacity-[0.55] z-50 transition-opacity mix-blend-multiply"
        show={modalOpen}
        appear={true}
        enter="transition ease-out duration-200"
        enterStart="opacity-0"
        enterEnd="opacity-100"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        aria-hidden="true"
      />
      {/* Modal dialog */}
      <Transition
        className="fixed md:inset-0 inset-x-0 top-16 bottom-0 z-50 overflow-hidden flex items-center justify-center"
        aria-modal="true"
        show={modalOpen}
        appear={true}
        enter="transition ease-out duration-300"
        enterStart="translate-y-full"
        enterEnd="translate-y-0"
        leave="transition ease-in duration-300"
        leaveStart="translate-y-0"
        leaveEnd="translate-y-full"
      >
        <div
          id={id}
          ref={modalContent}
          className={classNames(
            'bg-white flex rounded-t-xl shadow-lg mx-auto w-full max-h-full mt-auto md:mb-auto md:rounded-2xl select-none',
            size ? (size === 'sm' && 'md:max-w-[500px]') : 'md:max-w-[640px]',
          )}
          style={{
            transform: isMobile && `translateY(${positionY}px)`,
            cursor: !hideClose && isMobile && (dragging.current ? 'grabbing' : 'grab'),
          }}
          onTouchStart={handleTouchStart}
        >
          <div className="pt-8 md:pt-5 pb-8 relative w-full">
            <div className="px-4 md:px-6 h-full overflow-auto scrollbox">
              {/* Close button */}
              {!hideClose && (
                <button
                  className="hidden md:flex w-5 h-5 items-center justify-center absolute z-10 top-4 right-4 text-brand-green hover:opacity-70 transition duration-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalOpen(false);
                    onModalClose();
                  }}
                >
                  <div className="sr-only">Close</div>
                  <Close className="w-full h-full" />
                </button>
              )}
              {!hideClose && <div className="block md:hidden w-10 h-1 rounded-full bg-[#E8E8E8] absolute z-[1] inset-x-0 top-2 bottom-auto m-auto"></div>}
              <div className="relative">{children}</div>
            </div>
          </div>
        </div>
      </Transition>
    </>
  );
}

export default ModalAction;
